<template>
  <div class="handout-detail">
    <el-card shadow="never" class="header-card">
      <el-alert
        v-if="isTeacherMode && !handoutEditable && form.id"
        type="info"
        :closable="false"
        show-icon
        style="margin-bottom:12px"
      >
        <template slot="title">
          该讲义由管理员或其他教师编写，您仅可查看和导出，不可编辑。可复制讲义 ID（{{ form.id }}）到「新建讲义」中引用导入。
        </template>
      </el-alert>
      <div class="header-row">
        <div class="title-zone">
          <el-button type="text" icon="el-icon-arrow-left" @click="$router.push(listPath)">返回</el-button>
          <span class="page-title">{{ form.title || '讲义编辑' }}</span>
          <el-tag size="small" :type="statusType(form.status)">{{ statusLabel(form.status) }}</el-tag>
          <el-tag v-if="form.versionNo" size="mini" type="info">{{ form.versionNo }}</el-tag>
        </div>
        <div class="header-actions">
          <span class="question-count">共 {{ questions.length }} 题</span>
          <el-button v-if="handoutEditable" size="small" icon="el-icon-setting" @click="baseDialogVisible = true">基础信息</el-button>
          <span v-if="exportQuotaLabel" class="export-quota-label">{{ exportQuotaLabel }}</span>
          <el-button size="small" icon="el-icon-document" @click="exportHandout('student', 'docx')">学生版 Word</el-button>
          <el-button size="small" icon="el-icon-document-checked" @click="exportHandout('teacher', 'docx')">教师版 Word</el-button>
          <el-button size="small" icon="el-icon-tickets" @click="exportHandout('student', 'pdf')">学生版 PDF</el-button>
          <el-button size="small" icon="el-icon-tickets" @click="exportHandout('teacher', 'pdf')">教师版 PDF</el-button>
          <el-button v-if="handoutEditable" size="small" :type="form.status === 1 ? 'warning' : 'success'" @click="toggleStatus">{{ form.status === 1 ? '下架' : '发布' }}</el-button>
          <el-button v-if="handoutEditable" size="small" type="primary" :loading="saving" @click="saveHandout">保存</el-button>
        </div>
      </div>
    </el-card>

    <div class="detail-layout">
      <div class="outline-side">
        <div class="outline-card">
          <div class="outline-title">
            <span>目录</span>
            <el-button type="text" icon="el-icon-refresh" @click="$forceUpdate()" />
          </div>
          <div class="outline-list">
            <el-empty v-if="outlineItems.length === 0" description="暂无目录" :image-size="48">
              <span class="outline-empty-tip">填写讲义名称，并将正文段落设为标题1/2/3后显示</span>
            </el-empty>
            <div v-else>
              <div
                v-for="item in outlineItems"
                :key="item.key"
                class="outline-item"
                :class="['level-' + item.level, { 'is-root': item.level === 0 }]"
                @click="focusContentEditor"
              >
                {{ item.text }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="edit-main">
        <el-card shadow="never" class="content-card">
          <div slot="header" class="card-header">
            <span>讲义正文</span>
            <div class="content-actions" v-if="handoutEditable">
              <span class="inline-switch">
                <span>来源</span>
                <el-switch v-model="showQuestionSource" size="mini" @change="syncQuestionMetaVisibility" />
              </span>
              <span class="inline-switch">
                <span>星级</span>
                <el-switch v-model="showQuestionDifficulty" size="mini" @change="syncQuestionMetaVisibility" />
              </span>
              <el-button size="mini" icon="el-icon-document-add" @click="insertPageBreak">插入分页</el-button>
              <el-button size="mini" type="primary" plain icon="el-icon-document-add" @click="insertTemplate">插入结构</el-button>
            </div>
          </div>
          <RichEditor
            ref="contentEditor"
            v-model="form.contentHtml"
            class="handout-rich-editor"
            height="100%"
            :extended="true"
            :handout-mode="true"
            :preview-only="!handoutEditable"
            :handout-title="form.title"
            :show-question-source="showQuestionSource"
            :show-question-difficulty="showQuestionDifficulty"
            placeholder="填写学习目标、知识梳理、经典例题、课堂练习、课后作业等内容"
            @editor-blur="onContentEditorBlur"
            @option-layout-change="refreshSidebarPreview"
          />
        </el-card>

        <el-card shadow="never" class="body-order-card" v-if="handoutEditable">
          <div slot="header" class="card-header">
            <span>正文题目顺序</span>
            <el-button type="text" size="mini" icon="el-icon-refresh" @click="refreshBodyQuestionBlocks">刷新</el-button>
          </div>
          <el-empty v-if="!bodyQuestionBlocks.length" description="正文中暂无题目，请从下方试题篮插入" :image-size="56" />
          <div v-else class="body-order-list">
            <div
              v-for="(item, index) in bodyQuestionBlocks"
              :key="item.blockId"
              class="body-order-item"
            >
              <span class="body-order-index">{{ index + 1 }}</span>
              <div class="body-order-main">
                <div class="body-order-title" :title="item.title">{{ item.title }}</div>
                <div class="body-order-move">
                  <span>移至第</span>
                  <el-input-number
                    :value="index + 1"
                    :min="1"
                    :max="bodyQuestionBlocks.length"
                    size="mini"
                    controls-position="right"
                    @change="val => { if (val !== index + 1) moveBodyQuestionToPosition(index, val) }"
                  />
                  <span>位</span>
                </div>
              </div>
              <div class="body-order-actions">
                <el-button type="text" icon="el-icon-top" :disabled="index === 0" title="上移" @click="moveBodyQuestion(index, -1)" />
                <el-button type="text" icon="el-icon-bottom" :disabled="index === bodyQuestionBlocks.length - 1" title="下移" @click="moveBodyQuestion(index, 1)" />
                <el-button type="text" icon="el-icon-delete" class="danger-text" title="从正文移除" @click="removeBodyQuestion(item)" />
              </div>
            </div>
          </div>
        </el-card>

        <el-card shadow="never" class="question-card">
          <div slot="header" class="card-header">
            <span>试题篮</span>
            <el-button v-if="handoutEditable" size="small" type="primary" icon="el-icon-plus" @click="openAddDialog">从题库添加</el-button>
          </div>
          <el-empty v-if="questions.length === 0" description="暂无配套题目" />
          <div v-for="(q, index) in questions" :key="q.id" class="question-row">
            <span class="question-index">{{ index + 1 }}</span>
            <div class="question-body">
              <div class="question-meta">
                <el-tag size="mini">{{ typeMap[q.type] || q.type }}</el-tag>
                <DifficultyStars :value="q.difficulty" readonly compact />
                <span v-if="q.knowledgeTags">{{ q.knowledgeTags }}</span>
              </div>
              <div class="rich-html question-content" v-html="renderHtml(q.content)"></div>
              <div class="options" v-if="parseOptions(q.optionsJson).length">
                <div v-for="opt in parseOptions(q.optionsJson)" :key="opt.key" class="option-item">
                  <span class="option-key">{{ opt.key }}.</span>
                  <div class="rich-html option-value" v-html="renderHtml(opt.value)"></div>
                </div>
              </div>
            </div>
            <div class="question-actions" v-if="handoutEditable">
              <el-button type="text" icon="el-icon-document-add" @click="insertQuestionToContent(q)">插入正文</el-button>
              <el-button type="text" icon="el-icon-top" :disabled="index === 0" @click="moveQuestion(index, -1)">上移</el-button>
              <el-button type="text" icon="el-icon-bottom" :disabled="index === questions.length - 1" @click="moveQuestion(index, 1)">下移</el-button>
              <el-button type="text" icon="el-icon-delete" class="danger-text" @click="removeQuestion(q)">移除</el-button>
            </div>
          </div>
        </el-card>

      </div>

      <div class="preview-side">
        <el-card shadow="never" class="preview-card" @click.native="openPreviewPage">
          <div slot="header" class="card-header">
            <span>讲义预览</span>
            <el-button type="text" size="mini" icon="el-icon-view" @click.stop="openPreviewPage">预览</el-button>
          </div>
          <div class="preview-handout-shell" @click.stop>
            <div
              ref="previewScaleHost"
              class="preview-scale-host"
              :style="previewScaleHostStyle"
            >
              <div class="preview-scale-inner" :style="previewScaleInnerStyle">
                <HandoutHtmlPreview
                  ref="sidebarHandoutPreview"
                  embedded
                  :title="form.title"
                  :content-html="sidebarPreviewHtml"
                  :preferred-content-width="sidebarPreviewContentWidth"
                  :show-question-source="showQuestionSource"
                  :show-question-difficulty="showQuestionDifficulty"
                  @layout="onSidebarPreviewLayout"
                />
              </div>
            </div>
          </div>
        </el-card>
      </div>
    </div>

    <el-dialog title="基础信息" :visible.sync="baseDialogVisible" width="760px">
      <el-form ref="form" :model="form" :rules="rules" label-width="86px">
        <el-form-item label="讲义名称" prop="title">
          <el-input v-model="form.title" />
        </el-form-item>
        <el-row :gutter="12">
          <el-col :span="8">
            <el-form-item label="学年">
              <el-select v-model="form.schoolYearId" clearable style="width:100%">
                <el-option v-for="item in schoolYears" :key="item.id" :label="item.name" :value="item.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="年级" prop="gradeId">
              <el-select v-model="form.gradeId" clearable style="width:100%" @change="onGradeChange">
                <el-option v-for="item in grades" :key="item.id" :label="item.name" :value="item.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="学科" prop="subjectId">
              <el-select v-model="form.subjectId" clearable style="width:100%">
                <el-option v-for="item in subjects" :key="item.id" :label="item.name" :value="item.id" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="8">
            <el-form-item label="学期">
              <el-select v-model="form.semesterId" clearable style="width:100%">
                <el-option v-for="item in semesters" :key="item.id" :label="item.name" :value="item.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="课型">
              <el-select v-model="form.lessonType" clearable style="width:100%">
                <el-option v-for="item in lessonTypes" :key="item.value" :label="item.label" :value="item.value" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="版本">
              <el-input v-model="form.versionNo" placeholder="v1.0" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="8">
            <el-form-item label="主备人">
              <el-input v-model="form.authorName" />
            </el-form-item>
          </el-col>
          <el-col :span="16">
            <el-form-item label="讲义说明">
              <el-input v-model="form.summary" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <div slot="footer">
        <el-button @click="baseDialogVisible = false">关闭</el-button>
        <el-button type="primary" :loading="saving" @click="saveHandout">保存</el-button>
      </div>
    </el-dialog>

    <HandoutExportPayDialog :visible.sync="exportPayVisible" @paid="onExportPaid" />
  </div>
</template>

<script>
import filterLoader from '../../mixins/filterLoader'
import apiContext from '../../mixins/apiContext'
import handoutExportBilling from '../../mixins/handoutExportBilling'
import { renderHtml } from '../../utils/renderHtml'
import { parseQuestionOptions } from '../../utils/questionOptions'
import { stripHandoutPaginationComment, extractHandoutPaginationPlan, embedHandoutPaginationComment, encodePaginationPlanParam } from '../../utils/handoutPagination'
import { saveHandoutPreviewCache } from '../../utils/handoutPreviewCache'
import { downloadHandoutExport } from '../../utils/handoutExportDownload'
import { ExportQuotaError } from '../../utils/teacherExportBilling'
import teacherRequest from '../../utils/teacherRequest'
import { canTeacherEditHandout } from '../../utils/teacherOwnership'
import { buildQuestionIndexHtml, buildQuestionMetaInnerHtml, extractQuestionMetaPrefix, isQuestionMetaText, splitQuestionMetaLine, stripHandoutQuestionKnowledgeSpans } from '../../utils/handoutDecorate'
import { getQuestionBlockRangesFromHtml, moveQuestionBlockByIndexInHtml, removeQuestionBlockFromHtml, renumberQuestionsInHtml } from '../../utils/handoutQuestionBlocks'
import {
  buildOptionParagraphHtml,
  ensureOptionLayoutsInHtml,
  mergeLayoutMapsPreferPlan,
  applyOptionLayoutsFromPlan
} from '../../utils/handoutOptionLayout'
import '../../styles/handout-headings.css'
import RichEditor from '../../components/RichEditor.vue'
import HandoutHtmlPreview from '../../components/HandoutHtmlPreview.vue'
import DifficultyStars from '../../components/DifficultyStars.vue'

export default {
  name: 'HandoutDetail',
  components: { RichEditor, HandoutHtmlPreview, DifficultyStars },
  mixins: [filterLoader, apiContext, handoutExportBilling],
  data() {
    return {
      form: { title: '', summary: '', contentHtml: '', versionNo: 'v1.0', status: 0 },
      lastSavedContentHtml: '',
      lastSavedBodyHtml: '',
      showQuestionSource: true,
      showQuestionDifficulty: true,
      questions: [],
      bodyQuestionBlocks: [],
      schoolYears: [],
      grades: [],
      semesters: [],
      subjects: [],
      stages: [],
      saving: false,
      sidebarPreviewHtml: '',
      sidebarPreviewLayoutHeight: 600,
      sidebarPreviewScale: 0.45,
      sidebarPreviewContentWidth: 760,
      _sidebarPreviewPending: false,
      baseDialogVisible: false,
      addDialogVisible: false,
      addLoading: false,
      addFilter: {
        gradeId: null, subjectId: null, stageId: null, type: null, keyword: '',
        knowledgeTagPath: [], knowledgeTagNodeId: null,
        questionTagPath: [], questionTagNodeId: null
      },
      addQuestions: [],
      addPage: 0,
      addSize: 10,
      addTotal: 0,
      basketItems: [],
      knowledgeTagOptions: [],
      questionTagOptions: [],
      tagProps: { value: 'value', label: 'label', children: 'children', checkStrictly: true, emitPath: true },
      orderSaving: false,
      me: null,
      lessonTypes: [
        { value: 'NEW', label: '新授课' },
        { value: 'REVIEW', label: '复习课' },
        { value: 'PRACTICE', label: '习题课' },
        { value: 'EXAM_REVIEW', label: '试卷讲评' }
      ],
      typeMap: { SINGLE: '单选题', MULTI: '多选题', FILL: '填空题', QA: '问答题' },
      rules: {
        title: [{ required: true, message: '请输入讲义名称', trigger: 'blur' }],
        gradeId: [{ required: true, message: '请选择年级', trigger: 'change' }],
        subjectId: [{ required: true, message: '请选择学科', trigger: 'change' }]
      }
    }
  },
  computed: {
    handoutId() { return this.$route.params.id },
    handoutEditable() {
      if (!this.isTeacherMode) return true
      return canTeacherEditHandout(this.form, this.me)
    },
    listPath() { return this.isTeacherMode ? '/teacher/handouts' : '/handout/list' },
    handoutBasePath() { return this.isTeacherMode ? '/teacher/handout' : '/handout' },
    questionListPath() { return this.isTeacherMode ? '/questions/list' : '/question/list' },
    existingIds() { return this.questions.map(q => q.id) },
    outlineItems() {
      const items = []
      const rootTitle = (this.form.title || '').trim()
      if (rootTitle) {
        items.push({ key: 'handout-root-title', text: rootTitle, level: 0 })
      }
      const html = stripHandoutPaginationComment(this.form.contentHtml || '')
      if (!html || typeof DOMParser === 'undefined') return items
      const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html')
      const headings = Array.from(doc.querySelectorAll('h1,h2,h3,h4')).map((node, index) => ({
        key: `${node.tagName}-${index}-${node.textContent}`,
        text: (node.textContent || '').trim(),
        level: Math.min(Number(node.tagName.replace('H', '')) || 1, 4)
      })).filter(item => item.text)
      return items.concat(headings)
    },
    previewScaleHostStyle() {
      const height = Math.ceil(this.sidebarPreviewLayoutHeight * this.sidebarPreviewScale)
      return { height: `${Math.max(120, height)}px` }
    },
    previewScaleInnerStyle() {
      const scale = this.sidebarPreviewScale
      const width = this.sidebarPreviewContentWidth
      return {
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
        width: `${width}px`
      }
    }
  },
  watch: {
    'form.contentHtml'() {
      this.scheduleSidebarPreviewRefresh()
    },
    'form.title'() {
      this.scheduleSidebarPreviewRefresh()
    },
    showQuestionSource() {
      this.syncQuestionMetaVisibility()
    },
    showQuestionDifficulty() {
      this.syncQuestionMetaVisibility()
    }
  },
  created() {
    if (this.isTeacherMode) {
      teacherRequest.get('/me').then(data => { this.me = data }).catch(() => {})
    }
    this.loadGradeAndSubject()
    this.loadStageDictBundle(null)
    this.loadDictLookup()
    this.loadStages()
    this.loadDetail()
  },
  mounted() {
    this.$nextTick(() => {
      this.updateSidebarPreviewScale()
      if (typeof ResizeObserver !== 'undefined') {
        this._previewResizeObs = new ResizeObserver(() => this.updateSidebarPreviewScale())
        if (this.$refs.previewScaleHost) {
          this._previewResizeObs.observe(this.$refs.previewScaleHost)
        }
      }
      this.refreshSidebarPreview()
    })
  },
  beforeDestroy() {
    if (this._previewResizeObs) this._previewResizeObs.disconnect()
    if (this._sidebarPreviewRefreshTimer) clearTimeout(this._sidebarPreviewRefreshTimer)
  },
  methods: {
    renderHtml,
    questionMetaVisibilityStorageKey() {
      return `handout:${this.handoutId}:questionMetaVisibility`
    },
    saveQuestionMetaVisibilityPreference() {
      try {
        localStorage.setItem(this.questionMetaVisibilityStorageKey(), JSON.stringify({
          showSource: this.showQuestionSource,
          showDifficulty: this.showQuestionDifficulty
        }))
      } catch (e) {}
    },
    applyQuestionMetaVisibilityToHtml(html) {
      if (!html || typeof DOMParser === 'undefined') return html
      const plan = extractHandoutPaginationPlan(html)
      const bodyHtml = stripHandoutPaginationComment(html)
      const doc = new DOMParser().parseFromString(`<div id="root">${bodyHtml}</div>`, 'text/html')
      const root = doc.getElementById('root')
      if (!root) return html
      const setDisplay = (selector, visible) => {
        Array.from(root.querySelectorAll(selector)).forEach(node => {
          if (visible) {
            node.style.removeProperty('display')
          } else {
            node.style.display = 'none'
          }
        })
      }
      setDisplay('[data-handout-question-source],.handout-question-source', this.showQuestionSource)
      setDisplay('[data-handout-question-difficulty],.handout-question-difficulty', this.showQuestionDifficulty)
      stripHandoutQuestionKnowledgeSpans(root)
      const normalized = root.innerHTML.trim()
      return plan ? embedHandoutPaginationComment(normalized, plan) : normalized
    },
    focusContentEditor() {
      this.$refs.contentEditor && this.$refs.contentEditor.focus()
    },
    isContentEditorFocused() {
      const editor = this.$refs.contentEditor
      return !!(editor && typeof editor.isEditorFocused === 'function' && editor.isEditorFocused())
    },
    onContentEditorBlur() {
      this.refreshBodyQuestionBlocks()
      if (this._sidebarPreviewPending) {
        this._sidebarPreviewPending = false
        this.refreshSidebarPreview()
        return
      }
      this.scheduleSidebarPreviewRefresh()
    },
    scheduleSidebarPreviewRefresh() {
      if (this.isContentEditorFocused()) {
        this._sidebarPreviewPending = true
        return
      }
      if (this._sidebarPreviewRefreshTimer) clearTimeout(this._sidebarPreviewRefreshTimer)
      this._sidebarPreviewRefreshTimer = setTimeout(() => {
        if (this.isContentEditorFocused()) {
          this._sidebarPreviewPending = true
          return
        }
        this.refreshSidebarPreview()
      }, 1200)
    },
    refreshSidebarPreview() {
      this.$nextTick(() => {
        const editor = this.$refs.contentEditor
        let html = this.form.contentHtml || ''
        if (editor && typeof editor.buildHandoutPaginationSnapshot === 'function') {
          try {
            html = editor.buildHandoutPaginationSnapshot()
          } catch (e) {}
        }
        if (editor && typeof editor.getEditableRoot === 'function') {
          const root = editor.getEditableRoot()
          if (root && root.clientWidth > 0) {
            this.sidebarPreviewContentWidth = Math.round(root.clientWidth)
          }
        }
        if (html !== this.sidebarPreviewHtml) {
          this.sidebarPreviewHtml = html
        }
        const plan = extractHandoutPaginationPlan(html)
        if (plan && plan.contentWidth > 0) {
          this.sidebarPreviewContentWidth = plan.contentWidth
        }
        this.updateSidebarPreviewScale()
      })
    },
    onSidebarPreviewLayout(payload) {
      const data = payload && typeof payload === 'object' ? payload : {}
      if (data.height > 0) this.sidebarPreviewLayoutHeight = data.height
      if (data.contentWidth > 0) this.sidebarPreviewContentWidth = data.contentWidth
      this.updateSidebarPreviewScale()
    },
    updateSidebarPreviewScale() {
      const host = this.$refs.previewScaleHost
      if (!host) return
      const width = this.sidebarPreviewContentWidth || 760
      const available = host.clientWidth || (host.parentElement && host.parentElement.clientWidth) || 280
      this.sidebarPreviewScale = Math.min(1, Math.max(0.2, available / width))
    },
    openPreviewPage() {
      const editor = this.$refs.contentEditor
      const currentBody = stripHandoutPaginationComment(
        editor && typeof editor.getHtml === 'function'
          ? editor.getHtml()
          : (this.form.contentHtml || '')
      )
      const savedBody = this.lastSavedBodyHtml || stripHandoutPaginationComment(this.lastSavedContentHtml || '')
      if (currentBody !== savedBody) {
        this.$message.warning('正文有未保存的修改，预览将显示当前编辑内容')
      }
      const previewHtml = editor && typeof editor.buildHandoutPaginationSnapshot === 'function'
        ? editor.buildHandoutPaginationSnapshot()
        : (this.form.contentHtml || '')
      this.saveQuestionMetaVisibilityPreference()
      const route = {
        path: `${this.handoutBasePath}/${this.handoutId}/preview`,
        query: {
          showSource: this.showQuestionSource ? 'true' : 'false',
          showDifficulty: this.showQuestionDifficulty ? 'true' : 'false',
          alignBodyPages: 'true'
        }
      }
      this.$router.push(route).catch(() => {})
      requestAnimationFrame(() => {
        saveHandoutPreviewCache(this.handoutId, {
          title: this.form.title || '',
          contentHtml: previewHtml
        })
      })
    },
    loadDetail() {
      this.api.get(`/handout/${this.handoutId}`).then(data => {
        const handout = { ...data.handout }
        handout.contentHtml = this.normalizeQuestionMetaHtml(handout.contentHtml || '')
        this.form = handout
        this.lastSavedContentHtml = this.form.contentHtml || ''
        this.lastSavedBodyHtml = stripHandoutPaginationComment(this.lastSavedContentHtml)
        this.questions = data.questions || []
        this.consumePickerResult()
        if (this.form.gradeId) {
          this.onGradeChange(this.form.gradeId)
        }
        this.$nextTick(() => {
          this.syncQuestionMetaVisibility(false)
          this.repairLegacyQuestionDifficulties()
          this.refreshSidebarPreview()
          this.refreshBodyQuestionBlocks()
          const editor = this.$refs.contentEditor
          if (editor && typeof editor.flushHandoutEditorLayout === 'function') {
            editor.flushHandoutEditorLayout()
            setTimeout(() => editor.flushHandoutEditorLayout(), 200)
          }
        })
      })
    },
    loadStages() {
      this.api.get('/stage').then(data => { this.stages = data || [] })
    },
    onGradeChange(gradeId) {
      const grade = this.grades.find(item => item.id === gradeId)
      return this.loadStageDictBundle(grade && grade.stageId ? grade.stageId : null)
    },
    saveHandout() {
      const doSave = () => {
        const editor = this.$refs.contentEditor
        let snapshot = this.form.contentHtml || ''
        let savedBody = ''
        if (editor && typeof editor.buildHandoutPaginationSnapshot === 'function') {
          snapshot = editor.buildHandoutPaginationSnapshot()
          savedBody = stripHandoutPaginationComment(snapshot)
          this.form.contentHtml = snapshot
        }
        this.saving = true
        this.api.put(`/handout/${this.handoutId}`, this.form).then(data => {
          const handout = { ...data }
          handout.contentHtml = this.normalizeQuestionMetaHtml(snapshot)
          this.form = handout
          this.lastSavedContentHtml = handout.contentHtml || ''
          this.lastSavedBodyHtml = savedBody || stripHandoutPaginationComment(this.lastSavedContentHtml)
          saveHandoutPreviewCache(this.handoutId, {
            title: handout.title || this.form.title || '',
            contentHtml: this.lastSavedContentHtml
          })
          this.baseDialogVisible = false
          this.$message.success('讲义已保存')
          this.refreshSidebarPreview()
          this.$nextTick(() => {
            if (editor && typeof editor.setHtml === 'function') {
              editor.setHtml(this.form.contentHtml)
            }
            this.refreshBodyQuestionBlocks()
          })
        }).finally(() => { this.saving = false })
      }
      if (this.$refs.form && this.$refs.form.validate) {
        this.$refs.form.validate(valid => {
          if (valid) doSave()
        })
        return
      }
      if (!this.form.title || !this.form.gradeId || !this.form.subjectId) {
        this.baseDialogVisible = true
        this.$message.warning('请先补全讲义名称、年级和学科')
        return
      }
      doSave()
    },
    toggleStatus() {
      const status = this.form.status === 1 ? 0 : 1
      const label = status === 1 ? '发布' : '下架'
      this.$confirm(`确认${label}该讲义？`, '提示', { type: 'warning' }).then(() => {
        this.api.put(`/handout/${this.handoutId}/status`, { status }).then(data => {
          this.form = { ...data }
          this.$message.success(`已${label}`)
        })
      }).catch(() => {})
    },
    insertTemplate() {
      const template = [
        '<h1>一、学习目标</h1><p></p>',
        '<h1>二、知识梳理</h1><p></p><h2>（一）重点内容</h2><p></p>',
        '<h1>三、经典例题</h1><p></p><h2>（一）典型例题</h2><p></p>',
        '<h1>四、课堂练习</h1><p></p>',
        '<h1>五、课后作业</h1><p></p>'
      ].join('')
      const editor = this.$refs.contentEditor
      if (editor && typeof editor.insertHtml === 'function') {
        editor.insertHtml(template)
        this.$nextTick(() => {
          this.form.contentHtml = editor.getHtml()
          if (typeof editor.flushHandoutEditorLayout === 'function') {
            editor.flushHandoutEditorLayout()
          }
        })
        this.$message.success('已插入讲义结构')
        return
      }
      this.form.contentHtml = (this.form.contentHtml || '') + template
      this.$nextTick(() => {
        this.$refs.contentEditor && this.$refs.contentEditor.setHtml(this.form.contentHtml)
      })
      this.$message.success('已插入讲义结构')
    },
    insertPageBreak() {
      const pageBreak = [
        '<p class="handout-page-break-token" data-handout-page-break="true">【分页符】</p>',
        '<p><br></p>'
      ].join('')
      const editor = this.$refs.contentEditor
      if (editor && typeof editor.insertHtml === 'function') {
        editor.insertHtml(pageBreak)
        this.$nextTick(() => {
          if (typeof editor.flushHandoutEditorLayout === 'function') {
            editor.flushHandoutEditorLayout(true)
          } else if (typeof editor.refreshPageBreakVisualsOnly === 'function') {
            editor.refreshPageBreakVisualsOnly()
          } else if (typeof editor.decorateHandoutEditor === 'function') {
            editor.decorateHandoutEditor(true)
          }
          this.form.contentHtml = editor.getHtml()
        })
        this.$message.success('已插入分页符，正文中会另起一页显示')
        return
      }
      this.form.contentHtml = (this.form.contentHtml || '') + pageBreak
      this.$nextTick(() => {
        const editor = this.$refs.contentEditor
        if (editor && typeof editor.setHtml === 'function') {
          editor.setHtml(this.form.contentHtml)
        }
        this.$nextTick(() => {
          if (editor && typeof editor.flushHandoutEditorLayout === 'function') {
            editor.flushHandoutEditorLayout(true)
          } else if (editor && typeof editor.refreshPageBreakVisualsOnly === 'function') {
            editor.refreshPageBreakVisualsOnly()
          }
        })
      })
      this.$message.success('已插入分页符，正文中会另起一页显示')
    },
    syncQuestionMetaVisibility(updateModel = true) {
      this.saveQuestionMetaVisibilityPreference()
      this.form.contentHtml = this.normalizeQuestionMetaHtml(this.form.contentHtml || '')
      this.$nextTick(() => {
        const editor = this.$refs.contentEditor
        if (editor && typeof editor.setHtml === 'function') {
          editor.setHtml(this.form.contentHtml)
        }
        this.$nextTick(() => {
          if (editor && typeof editor.decorateHandoutEditor === 'function') {
            editor.decorateHandoutEditor()
          }
          if (updateModel && editor && typeof editor.getHtml === 'function') {
            this.form.contentHtml = editor.getHtml()
          }
          this.refreshSidebarPreview()
        })
      })
    },
    normalizeQuestionMetaHtml(html) {
      if (!html || typeof DOMParser === 'undefined') return html
      const plan = extractHandoutPaginationPlan(html)
      const bodyHtml = stripHandoutPaginationComment(html)
      const doc = new DOMParser().parseFromString(`<div id="root">${bodyHtml}</div>`, 'text/html')
      const root = doc.getElementById('root')
      if (!root) return html
      stripHandoutQuestionKnowledgeSpans(root)
      Array.from(root.querySelectorAll('p')).forEach(node => {
        const text = (node.textContent || '').replace(/\s+/g, ' ').trim()
        if (!text || !isQuestionMetaText(text)) return
        const keyword = this.findQuestionKeywordAfterMeta(node)
        node.classList.add('handout-inline-question')
        if (keyword) node.setAttribute('data-handout-meta-keyword', keyword)
        node.removeAttribute('data-handout-meta-normalized')
        const prefix = extractQuestionMetaPrefix(node)
        node.innerHTML = prefix + buildQuestionMetaInnerHtml(splitQuestionMetaLine(text), this.escapeHtml.bind(this))
      })
      const normalized = root.innerHTML.trim()
      const body = plan ? embedHandoutPaginationComment(normalized, plan) : normalized
      return ensureOptionLayoutsInHtml(renumberQuestionsInHtml(body))
    },
    findQuestionKeywordAfterMeta(node) {
      let current = node ? node.nextElementSibling : null
      while (current) {
        const text = this.stripHtml(current.innerHTML || current.textContent || '')
        if (text && !this.isQuestionMetaLine(text) && !/^[A-H][.、\s]/.test(text)) {
          return text.slice(0, 40)
        }
        current = current.nextElementSibling
      }
      return ''
    },
    isQuestionMetaLine(text) {
      return isQuestionMetaText(text)
    },
    repairLegacyQuestionDifficulties() {
      if (!this.form.contentHtml || typeof DOMParser === 'undefined') return
      const doc = new DOMParser().parseFromString(`<div id="root">${this.form.contentHtml}</div>`, 'text/html')
      const root = doc.getElementById('root')
      if (!root) return
      const metas = Array.from(root.querySelectorAll('.handout-inline-question')).map(node => {
        const difficulty = node.querySelector('[data-question-difficulty-legacy="true"]')
        const keyword = node.getAttribute('data-handout-meta-keyword') || this.findQuestionKeywordAfterMeta(node)
        const source = (node.querySelector('[data-handout-question-source]') || {}).textContent || ''
        return { node, difficulty, keyword, source }
      }).filter(item => item.difficulty && item.keyword && item.keyword.length >= 6)
      const keywords = Array.from(new Set(metas.map(item => item.keyword))).slice(0, 12)
      if (!keywords.length) return
      Promise.all(keywords.map(keyword => this.findQuestionByKeyword(keyword))).then(results => {
        const matched = new Map()
        results.forEach(item => {
          if (item && item.keyword && item.question) matched.set(item.keyword, item.question)
        })
        let changed = false
        metas.forEach(item => {
          const question = matched.get(item.keyword)
          if (!question) return
          const value = Math.max(0, Math.min(5, Number(question.difficulty) || 0))
          item.difficulty.textContent = this.difficultyStars(value)
          item.difficulty.setAttribute('data-question-difficulty-value', String(value))
          item.difficulty.removeAttribute('data-question-difficulty-legacy')
          changed = true
        })
        if (!changed) return
        this.form.contentHtml = root.innerHTML
        this.$nextTick(() => {
          const editor = this.$refs.contentEditor
          if (editor && typeof editor.setHtml === 'function') editor.setHtml(this.form.contentHtml)
          this.syncQuestionMetaVisibility(false)
        })
      }).catch(() => {})
    },
    findQuestionByKeyword(keyword) {
      return this.api.get(this.questionListPath, {
        params: { page: 0, size: 8, keyword, status: 1 }
      }).then(data => {
        const list = (data && data.content) || []
        const normalizedKeyword = this.normalizeSearchText(keyword)
        const question = list
          .map(q => ({ q, score: this.scoreQuestionMatch(q, normalizedKeyword) }))
          .filter(item => item.score > 0)
          .sort((a, b) => b.score - a.score)[0]
        return { keyword, question: question ? question.q : null }
      }).catch(() => ({ keyword, question: null }))
    },
    normalizeSearchText(text) {
      return this.stripHtml(text).replace(/\s+/g, '')
    },
    scoreQuestionMatch(question, normalizedKeyword) {
      if (!question || !normalizedKeyword) return 0
      const content = this.normalizeSearchText(question.content || '')
      if (!content) return 0
      if (content.includes(normalizedKeyword)) return 100 + normalizedKeyword.length
      const shortKeyword = normalizedKeyword.slice(0, 16)
      if (shortKeyword && content.includes(shortKeyword)) return 50 + shortKeyword.length
      return 1
    },
    openAddDialog() {
      this.$router.push(`${this.handoutBasePath}/${this.handoutId}/pick-questions`)
    },
    consumePickerResult() {
      const insertKey = `handout:${this.handoutId}:pendingInsert`
      const addedKey = `handout:${this.handoutId}:pickerAdded`
      try {
        const insertRaw = sessionStorage.getItem(insertKey)
        if (insertRaw) {
          sessionStorage.removeItem(insertKey)
          const items = JSON.parse(insertRaw)
          if (Array.isArray(items) && items.length) {
            this.insertQuestionsToContent(items, true)
            this.$message.success(`已插入正文 ${items.length} 题，请记得保存`)
          }
        }
        if (sessionStorage.getItem(addedKey)) {
          sessionStorage.removeItem(addedKey)
          this.loadQuestions()
        }
      } catch (e) {}
    },
    syncStageFromGrade() {
      if (!this.addFilter.gradeId) return
      const grade = this.grades.find(item => item.id === this.addFilter.gradeId)
      if (grade && grade.stageId) this.addFilter.stageId = grade.stageId
    },
    onAddFilterChange() {
      this.addPage = 0
      this.addFilter.knowledgeTagPath = []
      this.addFilter.knowledgeTagNodeId = null
      this.addFilter.questionTagPath = []
      this.addFilter.questionTagNodeId = null
      this.syncStageFromGrade()
      this.loadTagTrees()
      this.loadAddQuestions()
    },
    loadTagTrees() {
      this.loadTagTree('knowledge', 'knowledgeTagOptions')
      this.loadTagTree('question', 'questionTagOptions')
    },
    loadTagTree(tagType, target) {
      const params = { tagType }
      if (this.addFilter.subjectId) params.subjectId = this.addFilter.subjectId
      if (this.addFilter.stageId) params.stageId = this.addFilter.stageId
      this.api.get('/tag-tree', { params }).then(tree => {
        this[target] = this.buildCascaderOptions(tree || [])
      }).catch(() => { this[target] = [] })
    },
    buildCascaderOptions(nodes) {
      return (nodes || []).map(node => ({
        value: node.id,
        label: node.name,
        children: node.children && node.children.length ? this.buildCascaderOptions(node.children) : undefined
      }))
    },
    onKnowledgeTagChange(path) {
      this.addFilter.knowledgeTagNodeId = path && path.length ? path[path.length - 1] : null
      this.addPage = 0
      this.loadAddQuestions()
    },
    onQuestionTagChange(path) {
      this.addFilter.questionTagNodeId = path && path.length ? path[path.length - 1] : null
      this.addPage = 0
      this.loadAddQuestions()
    },
    loadAddQuestions() {
      this.addLoading = true
      const { gradeId, subjectId, stageId, keyword, type, knowledgeTagNodeId, questionTagNodeId } = this.addFilter
      this.api.get(this.questionListPath, {
        params: { page: this.addPage, size: this.addSize, gradeId, subjectId, stageId, keyword, type, knowledgeTagNodeId, questionTagNodeId, status: 1 }
      }).then(data => {
        this.addQuestions = data.content || []
        this.addTotal = data.totalElements || 0
      }).finally(() => { this.addLoading = false })
    },
    handleAddPageChange(val) { this.addPage = val - 1; this.loadAddQuestions() },
    isInBasket(id) { return this.basketItems.some(item => item.id === id) },
    toggleSelect(q) {
      const index = this.basketItems.findIndex(item => item.id === q.id)
      if (index >= 0) {
        this.basketItems.splice(index, 1)
      } else {
        this.basketItems.push(q)
      }
    },
    removeFromBasket(id) {
      this.basketItems = this.basketItems.filter(item => item.id !== id)
    },
    escapeHtml(text) {
      return String(text || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    stripHtml(text) {
      return String(text || '')
        .replace(/<style[\s\S]*?<\/style>/gi, '')
        .replace(/<script[\s\S]*?<\/script>/gi, '')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },
    isEmptyRichBlock(node) {
      if (!node || !node.matches || !node.matches('p,div')) return false
      if (node.querySelector('img,table')) return false
      const text = (node.textContent || '').replace(/\s|\u200b/g, '')
      if (text) return false
      return !String(node.innerHTML || '')
        .replace(/<br\s*\/?>/gi, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s|\u200b/g, '')
    },
    cleanQuestionInsertHtml(html) {
      const raw = String(html || '')
      if (!raw) return ''
      if (typeof DOMParser === 'undefined') {
        return raw.replace(/<(p|div)\b[^>]*>\s*(?:&nbsp;|\s|<br\s*\/?>)*<\/\1>/gi, '').trim()
      }
      const doc = new DOMParser().parseFromString(`<div id="root">${raw}</div>`, 'text/html')
      const root = doc.getElementById('root')
      if (!root) return raw.trim()
      Array.from(root.querySelectorAll('p,div')).forEach(node => {
        if (this.isEmptyRichBlock(node)) node.remove()
      })
      return root.innerHTML.trim()
    },
    inlineOptionHtml(html) {
      const cleaned = this.cleanQuestionInsertHtml(html)
      if (!cleaned) return ''
      if (typeof DOMParser === 'undefined') {
        return cleaned
          .replace(/^<(p|div)\b[^>]*>/i, '')
          .replace(/<\/(p|div)>$/i, '')
      }
      const doc = new DOMParser().parseFromString(`<div id="root">${cleaned}</div>`, 'text/html')
      const root = doc.getElementById('root')
      if (!root) return cleaned
      if (root.querySelector('table')) return root.innerHTML

      const childNodes = Array.from(root.childNodes).filter(node => {
        return node.nodeType === Node.ELEMENT_NODE || (node.textContent || '').trim()
      })
      if (childNodes.length === 1 && childNodes[0].nodeType === Node.ELEMENT_NODE && /^(P|DIV)$/i.test(childNodes[0].tagName)) {
        return childNodes[0].innerHTML.trim()
      }
      const allParagraphs = childNodes.length > 0 && childNodes.every(node => (
        node.nodeType === Node.ELEMENT_NODE && /^(P|DIV)$/i.test(node.tagName)
      ))
      if (allParagraphs) {
        return childNodes.map(node => node.innerHTML.trim()).filter(Boolean).join('<br>')
      }
      return root.innerHTML.trim()
    },
    markQuestionNode(node, blockId, questionId, title) {
      if (!node || node.nodeType !== 1) return
      node.setAttribute('data-handout-question-block', blockId)
      node.setAttribute('data-question-id', this.escapeHtml(questionId))
      node.setAttribute('data-question-title', this.escapeHtml(title))
    },
    compactQuestionBodyHtml(html, blockId, questionId, title) {
      const cleaned = this.cleanQuestionInsertHtml(html)
      if (!cleaned) return ''
      if (typeof DOMParser === 'undefined') return cleaned
      const doc = new DOMParser().parseFromString(`<div id="root">${cleaned}</div>`, 'text/html')
      const root = doc.getElementById('root')
      if (!root) return cleaned

      Array.from(root.children).forEach(node => this.markQuestionNode(node, blockId, questionId, title))
      Array.from(root.querySelectorAll('p,div')).forEach(node => {
        if (node.closest('table')) return
        node.style.margin = '0 0 4px'
        node.style.fontSize = '16px'
        node.style.lineHeight = '1.8'
        node.style.color = '#111827'
      })
      Array.from(root.querySelectorAll('table')).forEach(node => {
        node.style.margin = '4px 0 8px'
      })
      return root.innerHTML.trim()
    },
    difficultyStars(value) {
      const difficulty = Math.max(0, Math.min(5, Number(value) || 0))
      return '★'.repeat(difficulty)
    },
    buildQuestionContentHtml(q, index) {
      const typeLabel = this.escapeHtml(this.typeMap[q.type] || q.type || '讲义题目')
      const typeHtml = `<b><span class="handout-question-type" data-handout-question-type="true">${typeLabel}</span></b>`
      const sourceHtml = q.source
        ? `<b><span class="handout-question-source" data-handout-question-source="true" style="margin-left:8px;${this.showQuestionSource ? '' : 'display:none;'}">来源：${this.escapeHtml(q.source)}</span></b>`
        : ''
      const difficultyValue = Math.max(0, Math.min(5, Number(q.difficulty) || 0))
      const difficultyHtml = `<span class="handout-question-difficulty" data-handout-question-difficulty="true" data-question-difficulty-value="${difficultyValue}" style="margin-left:8px;white-space:nowrap;${this.showQuestionDifficulty ? '' : 'display:none;'}">${this.difficultyStars(difficultyValue)}</span>`
      const blockId = `q-${q.id}-${Date.now()}-${Math.random().toString(16).slice(2)}`
      const cleanedContentHtml = this.cleanQuestionInsertHtml(q.content || '')
      const title = this.stripHtml(cleanedContentHtml).slice(0, 36) || `题目 ${q.id}`
      const contentHtml = this.compactQuestionBodyHtml(cleanedContentHtml, blockId, q.id, title)
      const parsedOptions = this.parseOptions(q.optionsJson)
      const optionInner = parsedOptions.map(opt => (
        buildOptionParagraphHtml(
          { key: opt.key, valueHtml: this.inlineOptionHtml(opt.value) },
          blockId,
          this.escapeHtml(q.id),
          this.escapeHtml(title),
          this.escapeHtml.bind(this),
          1
        )
      )).join('')
      const options = optionInner || ''
      return [
        `<p class="handout-inline-question" data-handout-question-block="${blockId}" data-handout-option-layout="1" data-question-id="${this.escapeHtml(q.id)}" data-question-title="${this.escapeHtml(title)}" style="margin:14px 0 2px;font-size:15px;line-height:1.8;color:#6b7280;">${buildQuestionIndexHtml(index)}${typeHtml}${sourceHtml}${difficultyHtml}</p>`,
        contentHtml,
        options
      ].filter(Boolean).join('')
    },
    insertQuestionsToContent(list, silent = false) {
      const items = (list || []).filter(Boolean)
      if (!items.length) return
      const existingCount = getQuestionBlockRangesFromHtml(this.getHandoutEditorHtml()).ranges.length
      const html = items.map((item, index) => this.buildQuestionContentHtml(item, existingCount + index + 1)).join('')
      const editor = this.$refs.contentEditor
      if (editor && typeof editor.insertHtml === 'function') {
        editor.insertHtml(html)
        this.$nextTick(() => {
          const nextHtml = renumberQuestionsInHtml(editor.getHtml())
          if (nextHtml && nextHtml !== editor.getHtml()) {
            editor.setHtml(nextHtml)
          }
          this.form.contentHtml = editor.getHtml()
          this.refreshBodyQuestionBlocks()
        })
      } else {
        this.form.contentHtml = (this.form.contentHtml || '') + html
        this.$nextTick(() => {
          this.$refs.contentEditor && this.$refs.contentEditor.setHtml(this.form.contentHtml)
        })
      }
      if (!silent) {
        this.$message.success(`已插入正文 ${items.length} 题，请记得保存`)
      }
    },
    insertQuestionToContent(q) {
      this.insertQuestionsToContent([q])
    },
    getHandoutEditorHtml() {
      const editor = this.$refs.contentEditor
      if (editor && typeof editor.buildHandoutPaginationSnapshot === 'function') {
        return editor.buildHandoutPaginationSnapshot()
      }
      if (editor && typeof editor.getHtml === 'function') {
        return editor.getHtml()
      }
      return this.form.contentHtml || ''
    },
    refreshBodyQuestionBlocks() {
      const html = this.form.contentHtml || this.getHandoutEditorHtml()
      this.bodyQuestionBlocks = getQuestionBlockRangesFromHtml(html).ranges.map((item, index) => ({
        blockId: item.blockId,
        questionId: item.questionId,
        title: item.title || `正文题目 ${index + 1}`
      }))
    },
    applyContentHtmlToEditor(html) {
      const editor = this.$refs.contentEditor
      let plan = extractHandoutPaginationPlan(html)
      const editorPlan = editor && editor._handoutPaginationPlan
      if (editorPlan && editorPlan.optionLayouts) {
        plan = plan || {}
        plan = {
          ...plan,
          optionLayouts: mergeLayoutMapsPreferPlan(plan.optionLayouts, editorPlan.optionLayouts)
        }
        html = embedHandoutPaginationComment(stripHandoutPaginationComment(html), plan)
      }
      const numberedHtml = ensureOptionLayoutsInHtml(renumberQuestionsInHtml(html))
      this.form.contentHtml = numberedHtml
      if (editor && plan) {
        editor._handoutPaginationPlan = extractHandoutPaginationPlan(numberedHtml) || plan
      }
      this.$nextTick(() => {
        if (editor && typeof editor.setHtml === 'function') {
          editor.setHtml(numberedHtml)
        }
        this.$nextTick(() => {
          if (editor && typeof editor.decorateHandoutEditor === 'function') {
            editor.decorateHandoutEditor()
          }
          if (editor && typeof editor.getEditableRoot === 'function' && editor._handoutPaginationPlan) {
            const editRoot = editor.getEditableRoot()
            if (editRoot) {
              applyOptionLayoutsFromPlan(editRoot, editor._handoutPaginationPlan)
            }
          }
          if (editor && typeof editor.flushHandoutEditorLayout === 'function') {
            editor.flushHandoutEditorLayout()
          }
          this.refreshBodyQuestionBlocks()
          this.refreshSidebarPreview()
        })
      })
    },
    moveBodyQuestionByIndex(fromIndex, toIndex) {
      const total = this.bodyQuestionBlocks.length
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= total || toIndex >= total || fromIndex === toIndex) return
      const editor = this.$refs.contentEditor
      const position = fromIndex < toIndex ? 'after' : 'before'
      if (editor && typeof editor.moveHandoutQuestionBlockByIndex === 'function') {
        const snapshot = editor.moveHandoutQuestionBlockByIndex(fromIndex, toIndex, position)
        if (snapshot) {
          this.form.contentHtml = snapshot
          this.$nextTick(() => {
            this.refreshBodyQuestionBlocks()
            this.refreshSidebarPreview()
          })
          this.$message.success('题目顺序已调整，请记得保存')
          return
        }
      }
      const savedPlan = editor && editor._handoutPaginationPlan
        ? JSON.parse(JSON.stringify(editor._handoutPaginationPlan))
        : null
      const sourceHtml = this.getHandoutEditorHtml()
      let nextHtml = moveQuestionBlockByIndexInHtml(sourceHtml, fromIndex, toIndex, position)
      if (!nextHtml || nextHtml === sourceHtml) return
      if (savedPlan && savedPlan.optionLayouts) {
        const body = stripHandoutPaginationComment(nextHtml)
        let plan = extractHandoutPaginationPlan(nextHtml) || {}
        plan = {
          ...plan,
          optionLayouts: mergeLayoutMapsPreferPlan(plan.optionLayouts, savedPlan.optionLayouts)
        }
        nextHtml = ensureOptionLayoutsInHtml(embedHandoutPaginationComment(body, plan))
      }
      this.applyContentHtmlToEditor(nextHtml)
      this.$message.success('题目顺序已调整，请记得保存')
    },
    moveBodyQuestion(index, step) {
      if (!step) return
      this.moveBodyQuestionByIndex(index, index + step)
    },
    moveBodyQuestionToPosition(fromIndex, position) {
      if (!position || position < 1) return
      const toIndex = position - 1
      if (toIndex === fromIndex) return
      this.moveBodyQuestionByIndex(fromIndex, toIndex)
    },
    removeBodyQuestion(item) {
      if (!item || !item.blockId) return
      this.$confirm('确认从讲义正文中移除这道题？', '提示', { type: 'warning' }).then(() => {
        const nextHtml = removeQuestionBlockFromHtml(this.getHandoutEditorHtml(), item.blockId)
        this.applyContentHtmlToEditor(nextHtml)
        this.$message.success('已从正文移除，请记得保存')
      }).catch(() => {})
    },
    batchAdd() {
      const questionIds = this.basketItems.map(item => item.id).filter(id => !this.existingIds.includes(id))
      if (!questionIds.length) return
      this.api.post(`/handout/${this.handoutId}/questions/batch`, { questionIds }).then(data => {
        this.$message.success(`已加入 ${data.added || 0} 题`)
        this.basketItems = []
        this.loadQuestions()
      })
    },
    batchAddAndInsert() {
      const selected = [...this.basketItems]
      const questionIds = selected.map(item => item.id).filter(id => !this.existingIds.includes(id))
      if (!questionIds.length) return
      this.api.post(`/handout/${this.handoutId}/questions/batch`, { questionIds }).then(data => {
        this.insertQuestionsToContent(selected, true)
        this.$message.success(`已加入 ${data.added || 0} 题并插入正文，请记得保存`)
        this.basketItems = []
        this.loadQuestions()
      })
    },
    loadQuestions() {
      this.api.get(`/handout/${this.handoutId}/questions`).then(data => {
        this.questions = data || []
      })
    },
    removeQuestion(q) {
      this.$confirm('确认从讲义中移除该题？', '提示', { type: 'warning' }).then(() => {
        this.api.delete(`/handout/${this.handoutId}/questions/${q.id}`).then(() => {
          this.$message.success('已移除')
          this.questions = this.questions.filter(item => item.id !== q.id)
        })
      }).catch(() => {})
    },
    moveQuestion(index, step) {
      const target = index + step
      if (target < 0 || target >= this.questions.length || this.orderSaving) return
      const list = [...this.questions]
      const temp = list[index]
      list[index] = list[target]
      list[target] = temp
      this.questions = list
      this.saveQuestionOrder()
    },
    saveQuestionOrder() {
      const questionIds = this.questions.map(q => q.id)
      this.orderSaving = true
      this.api.put(`/handout/${this.handoutId}/questions/order`, { questionIds }).then(() => {
        this.$message.success('顺序已更新')
      }).catch(() => {
        this.loadQuestions()
      }).finally(() => { this.orderSaving = false })
    },
    async exportHandout(version, format = 'docx') {
      const formatLabel = format === 'pdf' ? 'PDF' : 'Word'
      const exec = async () => {
        const editor = this.$refs.contentEditor
        let html = this.form.contentHtml || ''
        if (editor && typeof editor.buildHandoutPaginationSnapshot === 'function') {
          html = editor.buildHandoutPaginationSnapshot()
        } else if (editor && typeof editor.getHtml === 'function') {
          html = editor.getHtml()
        }
        html = this.applyQuestionMetaVisibilityToHtml(this.normalizeQuestionMetaHtml(html))
        await downloadHandoutExport(this.handoutId, {
          version,
          format,
          query: this.buildExportQuery(version),
          title: this.form.title,
          contentHtml: html,
          apiBase: this.apiPrefix,
          onError: () => this.$message.error(`${formatLabel} 导出失败`),
          onQuotaExceeded: () => this.openExportPayDialog(exec)
        })
        this.$message.success(`${formatLabel} 已下载`)
        if (this.isTeacherMode) await this.loadExportQuota()
      }
      try {
        await exec()
      } catch (e) {
        if (!(e instanceof ExportQuotaError)) {
          this.$message.error(`${formatLabel} 下载失败`)
        }
      }
    },
    buildExportQuery(version) {
      const editor = this.$refs.contentEditor
      let html = this.form.contentHtml || ''
      if (editor && typeof editor.buildHandoutPaginationSnapshot === 'function') {
        try {
          html = editor.buildHandoutPaginationSnapshot()
        } catch (e) {}
      }
      const params = new URLSearchParams()
      params.set('version', version)
      params.set('showSource', this.showQuestionSource ? 'true' : 'false')
      params.set('showDifficulty', this.showQuestionDifficulty ? 'true' : 'false')
      params.set('blankPageBreak', 'false')
      params.set('appendQuestions', 'false')
      params.set('alignBodyPages', 'true')
      const planParam = encodePaginationPlanParam(html)
      if (planParam) params.set('paginationPlan', planParam)
      return params.toString()
    },
    parseOptions: parseQuestionOptions,
    statusLabel(status) { return ({ 0: '草稿', 1: '已发布', 2: '已归档' })[status] || '未知' },
    statusType(status) { return status === 1 ? 'success' : (status === 2 ? 'info' : 'warning') },
    lessonTypeLabel(value) {
      const item = this.lessonTypes.find(x => x.value === value)
      return item ? item.label : ''
    },
    getGradeName(id) { return (this.grades.find(x => x.id === id) || {}).name || this.getDictDisplayName(id) },
    getSubjectName(id) { return (this.subjects.find(x => x.id === id) || {}).name || this.getDictDisplayName(id) }
  }
}
</script>

<style scoped>
.handout-detail { height: 100vh; background: #f3f5f8; overflow: hidden; }
.header-card { height: 64px; border-radius: 0; border-left: 0; border-right: 0; position: relative; z-index: 9; }
.header-card ::v-deep .el-card__body { height: 100%; padding: 0 20px; display: flex; align-items: center; }
.header-row { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.title-zone { display: flex; align-items: center; gap: 8px; min-width: 0; }
.page-title { font-size: 16px; font-weight: 700; color: #1f2937; max-width: 520px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.header-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.export-quota-label { font-size: 12px; color: #909399; margin-right: 4px; }
.question-count { font-size: 13px; color: #6b7280; margin-right: 4px; }
.detail-layout { height: calc(100vh - 64px); padding: 12px; display: grid; grid-template-columns: 248px minmax(720px, 1fr) 360px; grid-template-rows: minmax(220px, auto) minmax(200px, auto) minmax(200px, .5fr); gap: 12px; align-items: stretch; overflow: hidden; }
.outline-side { grid-column: 1; grid-row: 1 / span 3; min-width: 0; min-height: 0; }
.outline-card { height: 100%; background: #fff; border: 1px solid #e5e7eb; border-radius: 4px; overflow: hidden; display: flex; flex-direction: column; }
.outline-title { height: 44px; padding: 0 12px; border-bottom: 1px solid #edf0f5; display: flex; align-items: center; justify-content: space-between; font-weight: 700; color: #1f2937; }
.outline-list { flex: 1; overflow: auto; padding: 10px 8px 16px; }
.outline-empty-tip { color: #8b95a5; font-size: 12px; }
.outline-item { height: 34px; line-height: 34px; padding: 0 10px; border-radius: 4px; color: #374151; font-size: 13px; cursor: pointer; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.outline-item.is-root { font-weight: 700; color: #1677ff; margin-bottom: 4px; }
.outline-item:hover { background: #eef6ff; color: #1677ff; }
.outline-item.level-1 { padding-left: 14px; font-weight: 600; }
.outline-item.level-2 { padding-left: 28px; color: #4b5563; }
.outline-item.level-3 { padding-left: 42px; color: #6b7280; font-size: 12px; }
.outline-item.level-4 { padding-left: 56px; color: #8b95a5; font-size: 12px; }
.edit-main { display: contents; }
.card-header { display: flex; align-items: center; justify-content: space-between; font-weight: 600; color: #1f2937; }
.content-actions { display: flex; align-items: center; gap: 8px; }
.inline-switch { display: inline-flex; align-items: center; gap: 5px; color: #6b7280; font-size: 12px; font-weight: 400; white-space: nowrap; }
.content-card { grid-column: 2; grid-row: 1 / span 3; min-width: 0; min-height: 0; display: flex; flex-direction: column; }
.body-order-card { grid-column: 3; grid-row: 1; min-width: 0; min-height: 0; display: flex; flex-direction: column; max-height: 280px; }
.question-card { grid-column: 3; grid-row: 2; min-width: 0; min-height: 0; display: flex; flex-direction: column; }
.content-card ::v-deep .el-card__body { flex: 1; min-height: 0; padding: 14px 18px 18px; display: flex; flex-direction: column; overflow: visible; }
.content-card ::v-deep .rich-editor { flex: 1; min-height: 0; overflow: visible; }
.preview-side { grid-column: 3; grid-row: 3; min-width: 0; min-height: 0; }
.preview-card { height: 100%; display: flex; flex-direction: column; cursor: pointer; transition: border-color .2s ease, box-shadow .2s ease; }
.preview-card:hover { border-color: #bcd7ff; box-shadow: 0 2px 10px rgba(31, 77, 133, 0.08); }
.content-card ::v-deep .el-card__header,
.body-order-card ::v-deep .el-card__header,
.question-card ::v-deep .el-card__header,
.preview-card ::v-deep .el-card__header { padding: 12px 14px; flex-shrink: 0; }
.body-order-card ::v-deep .el-card__body { flex: 1; min-height: 0; overflow: auto; padding: 8px 12px 12px; }
.body-order-list { display: flex; flex-direction: column; gap: 8px; }
.body-order-item { display: flex; align-items: flex-start; gap: 8px; padding: 10px; border: 1px solid #edf0f5; border-radius: 8px; background: #fafbfc; }
.body-order-index { width: 24px; height: 24px; border-radius: 50%; background: #1677ff; color: #fff; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 12px; font-weight: 600; }
.body-order-main { flex: 1; min-width: 0; }
.body-order-title { font-size: 12px; color: #374151; line-height: 1.5; margin-bottom: 6px; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
.body-order-move { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #6b7280; }
.body-order-move ::v-deep .el-input-number { width: 88px; }
.body-order-actions { display: flex; flex-direction: column; align-items: center; flex-shrink: 0; gap: 2px; }
.body-order-actions .el-button { padding: 2px 0; margin: 0; font-size: 16px; }
.preview-card ::v-deep .el-card__body { flex: 1; min-height: 0; overflow: auto; padding: 10px 12px; }
.preview-handout-shell {
  background: #eef1f5;
  border: 1px solid #e3e8f0;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);
  border-radius: 4px;
  overflow: visible;
}
.preview-scale-host {
  width: 100%;
  overflow: visible;
}
.preview-scale-inner {
  will-change: transform;
}
.question-row { display: flex; align-items: flex-start; gap: 10px; padding: 14px 0; border-bottom: 1px solid #f1f3f7; }
.question-index { width: 26px; height: 26px; border-radius: 50%; background: #1677ff; color: #fff; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.question-body { flex: 1; min-width: 0; }
.question-meta { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; color: #9ca3af; font-size: 12px; margin-bottom: 6px; }
.question-content { color: #1f2937; line-height: 1.6; }
.question-actions { width: 84px; display: flex; flex-direction: column; align-items: flex-end; flex-shrink: 0; }
.question-actions .el-button { padding: 3px 0; margin: 0; }
.question-card ::v-deep .el-card__body { flex: 1; min-height: 0; overflow: auto; padding: 8px 12px 12px; }
.question-card .question-row { gap: 8px; padding: 10px 0; }
.question-card .question-index { width: 22px; height: 22px; font-size: 12px; }
.question-card .question-meta { gap: 4px; margin-bottom: 4px; }
.question-card .question-content { max-height: 70px; overflow: hidden; font-size: 12px; }
.question-card .options { display: none; }
.question-card .question-actions { width: 72px; }
.options { background: #fafafa; border-radius: 4px; padding: 8px 10px; margin-top: 8px; }
.option-item { display: flex; gap: 4px; font-size: 13px; line-height: 1.5; }
.option-key { font-weight: 600; color: #374151; }
.option-value { flex: 1; min-width: 0; }
.option-value img { max-width: 100%; max-height: 52px; vertical-align: middle; display: inline; }
.danger-text { color: #f56c6c; }
.add-dialog-body { display: flex; gap: 12px; min-height: 480px; }
.add-main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.add-filter { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
.add-question-list { flex: 1; max-height: 420px; overflow-y: auto; }
.add-q-row { display: flex; gap: 8px; align-items: flex-start; border-bottom: 1px solid #f1f3f7; padding: 10px 8px; cursor: pointer; border-radius: 4px; }
.add-q-row:hover, .add-q-row.selected { background: #eef6ff; }
.add-q-row.added { opacity: .55; cursor: default; }
.add-q-check { width: 22px; padding-top: 2px; flex-shrink: 0; color: #52c41a; }
.add-q-content { flex: 1; min-width: 0; }
.add-q-text { line-height: 1.5; margin-bottom: 4px; }
.add-q-options {
  margin-bottom: 6px;
  padding: 6px 10px;
  background: #fafafa;
  border-radius: 4px;
}
.add-q-option-item {
  display: flex;
  align-items: flex-start;
  padding: 2px 0;
  font-size: 12px;
  color: #555;
  line-height: 1.4;
}
.add-q-option-key {
  font-weight: 600;
  color: #333;
  min-width: 18px;
  margin-right: 2px;
  flex-shrink: 0;
}
.add-q-option-value { flex: 1; min-width: 0; }
.add-q-option-value img { max-width: 100%; max-height: 80px; vertical-align: middle; }
.add-q-tags { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; color: #9ca3af; font-size: 12px; margin-top: 6px; }
.basket-panel { width: 270px; border-left: 1px solid #edf0f5; padding-left: 12px; display: flex; flex-direction: column; }
.basket-header { display: flex; align-items: center; justify-content: space-between; }
.basket-list { flex: 1; min-height: 120px; max-height: 410px; overflow: auto; }
.basket-item { display: flex; gap: 6px; align-items: flex-start; padding: 8px 0; border-bottom: 1px solid #f1f3f7; font-size: 12px; }
.basket-item-body { flex: 1; min-width: 0; }
.basket-text { line-height: 1.5; }
.basket-options {
  margin-top: 4px;
  padding: 4px 8px;
  background: #fafafa;
  border-radius: 4px;
}
.basket-option-item { display: flex; align-items: flex-start; padding: 1px 0; line-height: 1.4; }
.basket-option-key { font-weight: 600; min-width: 16px; margin-right: 2px; flex-shrink: 0; }
.basket-option-value { flex: 1; min-width: 0; }
.basket-option-value img { max-width: 100%; max-height: 48px; vertical-align: middle; }
@media (max-width: 1100px) {
  .handout-detail { height: auto; min-height: 100vh; overflow: auto; }
  .header-card { height: auto; min-height: 64px; }
  .header-card ::v-deep .el-card__body { padding: 10px 14px; }
  .detail-layout { height: auto; grid-template-columns: 1fr; grid-template-rows: auto; overflow: visible; }
  .outline-side, .preview-side { position: static; height: auto; }
  .outline-card { max-height: 240px; }
  .edit-main { display: flex; flex-direction: column; gap: 12px; }
  .content-card, .body-order-card, .question-card, .preview-side { grid-column: auto; grid-row: auto; max-height: none; }
  .content-card { min-height: 640px; }
  .question-card ::v-deep .el-card__body { max-height: none; }
}
</style>
